import {AKComponent} from '@theme/component';
import {debounce, isClickedOutside} from "@theme/utilities";
import {getFocusableElements, removeTrapFocus, trapFocus} from "@theme/focus";
import {
    MegaMenuSubmenuOpenedEvent,
    MegaMenuSubmenuClosedEvent,
    MegaMenuDrawerOpenedEvent,
    MegaMenuDrawerClosedEvent,
    MegaMenuDrawerPageChangedEvent
} from '@theme/events';

/* ********************************** */
/* ********** AK Mega Menu ********** */
/* ********************************** */
customElements.define('ak-mega-menu', class extends AKComponent {
    isOpen = false;
    activeSubmenu = '';
    isDrawerOpen = false;
    drawerElement = null;
    drawerPages = {};
    activeDrawer = '';
    lastDrawerPageOpen = null;

    constructor() {
        super();
        this.attr = {
            triggerType: 'click', // 'click' | 'mouseover'
            linkType: 'js', // 'js' | 'html'
        };
        this.data = {
            menu: [],
        }
        this.isDrawerOpen = false;
    }

    static get observedAttributes() {
        return ['drawer-id', 'trigger-type', 'link-type', 'data-menu'];
    }

    attributeChangedCallback(attribute, oldValue, newValue) {
        if (oldValue === newValue) return;
        const isData = attribute.substring(0, 5) === 'data-';

        if (isData) {
            const key = attribute.substring(5).replace(/-./g, x => x[1].toUpperCase());
            this.data[key] = JSON.parse(newValue);
        } else {
            const key = attribute.replace(/-./g, x => x[1].toUpperCase());
            this.attr[key] = newValue;
        }
    }

    /**
     * Déclencheur du sous-menu actuellement ouvert (lien ou bouton niveau 1).
     * @returns {HTMLElement | null}
     */
    /** @returns {HTMLElement[]} */
    getLvl1Items() {
        const raw = this.refs['lvl1'];
        if (!raw) return [];
        return Array.isArray(raw) ? raw : [raw];
    }

    getActiveSubmenuTrigger() {
        if (!this.activeSubmenu) return null;
        const li = this.getLvl1Items().find((ref) => {
            const c = ref.querySelector('a[data-submenu-target], button[data-submenu-target]');
            return c?.getAttribute('data-target-id') === this.activeSubmenu;
        });
        return li?.querySelector('a[data-submenu-target], button[data-submenu-target]') ?? null;
    }

    /**
     * Focus sur le déclencheur du sous-menu actif (lien ou bouton dans le li).
     * @param {HTMLElement | null} active
     * @param {HTMLElement | null} trigger
     * @returns {boolean}
     */
    _isSubmenuTriggerFocused(active, trigger) {
        if (!active || !trigger) return false;
        if (active === trigger) return true;
        return Boolean(trigger.contains(active));
    }

    /**
     * Flèche bas : entrer dans le panneau. Entrée : laisser le comportement natif du lien (aller sur l’URL).
     * Clic souris : idem, pas d’interception. Tab : ordre naturel. Shift+Tab depuis le 1er élément du panneau : retour au lien. Échap : fermer.
     * Capture uniquement pour Flèche bas / Échap / Shift+Tab (pas Entrée).
     * @param {KeyboardEvent} event
     */
    _handleMegaMenuKeydown = (event) => {
        if (!this.activeSubmenu || !this.refs['submenu']) return;

        const trigger = this.getActiveSubmenuTrigger();
        const panel = this.refs['submenu'].querySelector('.c-mm__submenu-panel.is-active');
        if (!panel || !trigger) return;

        const focusable = getFocusableElements(panel);
        if (focusable.length === 0) return;

        const first = focusable[0];
        const active = document.activeElement;
        const onTrigger = this._isSubmenuTriggerFocused(
            active instanceof HTMLElement ? active : null,
            trigger
        );

        if (event.key === 'Escape') {
            event.preventDefault();
            const id = this.activeSubmenu;
            this.closeSubmenu(id);
            trigger.focus();
            return;
        }

        const isArrowDown = event.key === 'ArrowDown' || event.code === 'ArrowDown';

        if (isArrowDown && onTrigger) {
            event.preventDefault();
            first.focus();
            return;
        }

        // Entrée : ne pas preventDefault — le lien principal reste activable (navigation vers la page catégorie).

        // Tab : ne pas entrer dans le sous-menu (parcours linéaire du header). Shift+Tab depuis le panneau : revenir au lien.
        if (event.key === 'Tab' && event.shiftKey && active === first) {
            event.preventDefault();
            trigger.focus();
        }
    };

    connectedCallback() {
        super.connectedCallback();

        // Vérifier que tous les templates requis sont présents
        if (!this.validateTemplates()) {
            console.warn('AK Mega Menu: Templates manquants. Le composant ne peut pas fonctionner correctement.');
            return;
        }

        // Capture : exécuté avant le lien <a> (sinon Entrée navigue, les flèches ne sont pas gérées).
        // Hors du if (lvl1) : les refs peuvent être peuplées après le 1er tick (MutationObserver).
        this.addEventListener('keydown', this._handleMegaMenuKeydown, true);

        if(this.refs['lvl1']) {
        this.refs['lvl1'].forEach(item => {
            const selector = item.getAttribute('data-type') === 'button' ? 'button' : 'a';
            const itemTargetId = item.querySelector(selector).getAttribute('data-target-id');
            const listeningEvent = this.attr.triggerType === 'click' ? 'click' : 'mouseover';

            if (listeningEvent === 'click') {
                item.addEventListener('click', (event) => {
                    if (this.attr.linkType === 'js') {
                        event.preventDefault();
                    }
                    this.switchSubmenu(itemTargetId);
                });
            }

            if (listeningEvent === 'mouseover') {
                item.addEventListener('mouseover', () => {
                    if (this.activeSubmenu === '') {
                        this.openSubmenu(itemTargetId);
                    }
                    this.close(itemTargetId);
                });
                item.addEventListener('mouseout', () => {
                    this.close();
                });
                // Clavier / lecteur d’écran : ouvrir comme au survol quand le focus entre sur l’item
                item.addEventListener('focusin', () => {
                    if (this.activeSubmenu === '') {
                        this.openSubmenu(itemTargetId);
                    }
                    this.close(itemTargetId);
                });
                item.addEventListener('focusout', (event) => {
                    const related = event.relatedTarget;
                    if (related && this.refs['submenu'].contains(related)) {
                        this.close.cancel();
                        return;
                    }
                    this.close();
                });
            }
        });

        this.refs['submenu'].addEventListener('mouseover', () => {
            this.close.cancel();
        })
        this.refs['submenu'].addEventListener('mouseout', () => {
            this.close();
        })
        this.refs['submenu'].addEventListener('focusin', () => {
            this.close.cancel();
        });
        this.refs['submenu'].addEventListener('focusout', (event) => {
            const related = event.relatedTarget;
            if (related && this.refs['submenu'].contains(related)) {
                return;
            }
            this.close();
        });

    }
        this.scrapToJson();
        this.createMobileDrawer();

        document.addEventListener('click', (event) => {
            if (this.activeSubmenu && isClickedOutside(event, this)) {
                this.closeSubmenu(this.activeSubmenu);
            }
            if (this.activeDrawer && isClickedOutside(event, this.drawerElement)) {
                this.closeDrawer();
            }
        })

        if (this.attr.linkType === 'js') {
            // this.trapFocusOnMainNav();
        }
    }

    /**
     * Valide que tous les templates requis sont présents
     * @returns {boolean} - true si tous les templates sont présents
     */
    validateTemplates() {
        const requiredTemplates = [
            'menu-li-template',
            'menu-button-template',
            'menu-link-template'
        ];

        const missingTemplates = [];

        requiredTemplates.forEach(templateId => {
            if (!this.querySelector(`#${templateId}`)) {
                missingTemplates.push(templateId);
            }
        });

        if (missingTemplates.length > 0) {
            console.warn(`AK Mega Menu: Templates manquants: ${missingTemplates.join(', ')}`);
            return false;
        }

        return true;
    }

    trapFocusOnMainNav() {
        return;
        trapFocus(this.querySelector(':scope > nav'));
        this.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                removeTrapFocus();
            }
        })
    }

    close = debounce((key) => {
        if (key) {
            this.openSubmenu(key);
        } else {
            this.closeSubmenu(this.activeSubmenu);
        }
    }, 300);

    scrapToJson() {
        const scrap = [];

        this.refs['nav'].querySelectorAll('li').forEach(item => {
            // const selector = item.getAttribute('data-type') === 'button' ? 'button' : 'a';
            const childElement = item.querySelector(':first-child');
            scrap.push({
                id: childElement.getAttribute('data-target-id'),
                title: childElement.innerHTML.trim(),
                link: childElement.href,
                link_obf: childElement.getAttribute('data-dfg'),
                children: [],
            });
        })
        const secondaryNav = document.querySelector('[ref="secondary-nav"]');
        if(secondaryNav) {
            secondaryNav.querySelectorAll('li').forEach(item => {
                const childElement = item.querySelector(':first-child');
                if (childElement) {
                    scrap.push({
                        id: null,
                        title: childElement.innerHTML.trim(),
                        link: childElement.href || '#',
                        link_obf: childElement.getAttribute('data-dfg'),
                        children: [],
                    });
                }
            })
        }
        if(this.refs['sublvl1']) {
        this.refs['sublvl1'].forEach(sublevel => {
            const subElementId = sublevel.getAttribute('data-id-link');

            sublevel.querySelectorAll('[ref="lvl2[]"]').forEach(lvl2 => {
                
                const sub = scrap.find(item => item.id === subElementId);
                // const selector = lvl2.getAttribute('data-type') === 'button' ? 'button' : 'a';
                const childElement = lvl2.querySelector(':first-child');
                sub['children'].push({
                    id: childElement.getAttribute('data-target-id'),
                    title: childElement.innerHTML.trim(),
                    link: childElement.href,
                    link_obf: childElement.getAttribute('data-dfg'),
                    children: this.scrapChildren(lvl2,'lvl3[]'),
                });
            })
        })
    }
        
        this.data.menu = scrap;
    }

    scrapChildren(parent, ref) {
        
        const children = [];
        parent.querySelectorAll('[ref="' + ref + '"]').forEach(item => {
            const childElement = item.querySelector(':first-child');
            children.push({
                id: null,
                title: childElement.innerHTML.trim(),
                link: childElement.href,
                link_obf: childElement.getAttribute('data-dfg'),
                children: [],
            });
        })
        return children;
    }

    switchSubmenu(id) {
        if (!this.isOpen) {
            this.openSubmenu(id);
        } else if (this.activeSubmenu === id) {
            this.closeSubmenu(id);
        } else {
            this.closeSubmenu(this.activeSubmenu);
            this.openSubmenu(id);
        }
    }

    openSubmenu(id) {
        const target = this.refs['sublvl1'].find(ref => ref.getAttribute('data-id-link') === id);
        if (id === '' || !target) {
            return;
        }
        if (this.activeSubmenu) {
            this.closeSubmenu(this.activeSubmenu);
        }
        this.refs['submenu'].open();
        this.refs['submenu'].setAttribute('aria-hidden', 'false');

        // target.style.display = 'block';
        target.classList.add('is-active');

        this.isOpen = true;
        this.activeSubmenu = id;

        this.refs['lvl1']?.forEach((ref) => {
            const control = ref.querySelector('a[data-submenu-target], button[data-submenu-target]');
            if (!control) return;
            const controlId = control.getAttribute('data-target-id');
            control.setAttribute('aria-expanded', controlId === id ? 'true' : 'false');
        });

        // trapFocus(target);
    }

    closeSubmenu(id) {
        const target = this.refs['sublvl1'].find(ref => ref.getAttribute('data-id-link') === id);
        if (id === '' || !target) {
            return;
        }
        this.refs['submenu'].close();
        this.refs['submenu'].setAttribute('aria-hidden', 'true');
        // target.style.display = 'none';
        target.classList.remove('is-active');
        this.isOpen = false;
        this.activeSubmenu = '';

        const trigger = this.refs['lvl1']?.find((ref) => {
            const control = ref.querySelector('a[data-submenu-target], button[data-submenu-target]');
            return control?.getAttribute('data-target-id') === id;
        })?.querySelector('a[data-submenu-target], button[data-submenu-target]');
        trigger?.setAttribute('aria-expanded', 'false');

        this.trapFocusOnMainNav();
    }

    switchDrawer() {
        if (this.isDrawerOpen) {
            this.closeDrawer();
        } else {
            this.openDrawer();
        }
    }

    /**
     * 
     * @param {*} id 
     */
    openDrawer(id = 'root') {
        this.refs['backdrop'].classList.add('is-active');
        this.drawerElement.setAttribute('aria-hidden', 'false');
        this.drawerElement.removeAttribute('tabindex');
        this.drawerElement.classList.add('is-active');
        this.isDrawerOpen = true;
        

        
        // Empêcher le scroll du body
        document.body.classList.add('has-drawer-open');
        
        this.toDrawerPage(id);
        
        // Activer le trap focus sur le drawer et focuser le premier élément
        trapFocus(this.drawerElement);
        
    }

    closeDrawer() {
        document.body.classList.remove('has-drawer-open');
        this.drawerElement.setAttribute('tabindex', '-1');
        this.drawerElement.setAttribute('aria-hidden', 'true');
        this.drawerElement.classList.remove('is-active');
        this.isDrawerOpen = false;
        this.activeDrawer = '';
        this.refs['backdrop'].classList.remove('is-active');

        
        // Désactiver le trap focus
        removeTrapFocus();
        
        this.cleanDrawerContent();
    }

    toDrawerPage(id) {
        // if (this.activeDrawer) {
        //     this.drawerPages[this.activeDrawer].back.style.display = 'none';
        //     this.drawerPages[this.activeDrawer].menu.style.display = 'none';
        // }
        // this.drawerPages[id].back.style.display = 'block';
        // this.drawerPages[id].menu.style.display = 'block';
        setTimeout(() => {
            this.activeDrawer = id;
        }, 0);
    }

    createMobileDrawer() {
        this.drawerElement = this.refs['mobile-drawer'];

        // Créer l'élément ul racine
        const ulElement = document.createElement('ul');
        ulElement.classList.add('c-mobile-drawer__list');
        ulElement.classList.add('--lvl1');
        ulElement.setAttribute('data-id-link', 'root');
        
        // Construire l'arbre du menu récursivement
        this.buildMenuTree(this.data.menu, ulElement, 2);
        
        this.refs['mobile-drawer-content'].prepend(ulElement);
        this.drawerPages['root'] = {menu: ulElement, back: null};
        this.setupMobileDrawerEvents();
    }

    /**
     * Construit l'arbre du menu de manière récursive
     * @param {Array} menuItems - Les éléments du menu
     * @param {HTMLElement} parentUl - L'élément ul parent
     * @param {number} level - Le niveau de profondeur
     */
    buildMenuTree(menuItems, parentUl, level) {
        menuItems.forEach(item => {
            const li = this.createMenuItem(item, level);
            parentUl.appendChild(li);
        });
    }

    /**
     * Crée un élément de menu (li) avec son contenu
     * @param {Object} item - L'élément du menu
     * @param {number} level - Le niveau de profondeur
     * @returns {HTMLElement} - L'élément li créé
     */
    createMenuItem(item, level) {
        // Créer le li avec le template
        const liTemplate = this.querySelector('#menu-li-template');
        const liElement = liTemplate.content.cloneNode(true);
        const li = liElement.querySelector('li');
        
        // Remplacer les placeholders du li
        li.innerHTML = li.innerHTML.replace('{{level}}', level);
        li.innerHTML = li.innerHTML.replace('{{itemId}}', item.id);
        
        // Si l'élément a des enfants, c'est un bouton
        if (item.children && item.children.length > 0) {
            const button = this.createButton(item, level);
            li.innerHTML = li.innerHTML.replace('{{content}}', button.outerHTML);
            
            // Créer le sous-menu IMBRIQUÉ dans le li
            const subUl = document.createElement('ul');
            subUl.classList.add('--lvl' + level);
            subUl.classList.add('c-mobile-drawer__list');
            subUl.setAttribute('data-id-link', item.id);
            
            // Construire récursivement les enfants
            this.buildMenuTree(item.children, subUl, level + 1);
            
            // Ajouter le sous-menu DANS le li (imbriqué)
            li.appendChild(subUl);
            
            
            
            // Stocker pour la navigation mobile
            this.drawerPages[item.id] = {menu: subUl};
            
        } else {
            // Sinon c'est un lien
            const link = this.createLink(item, level);
            li.innerHTML = li.innerHTML.replace('{{content}}', link.outerHTML);
        }
        
        return li;
    }

    /**
     * Crée un bouton pour un élément de menu avec enfants
     * @param {Object} item - L'élément du menu
     * @param {number} level - Le niveau de profondeur
     * @returns {HTMLElement} - L'élément button créé
     */
    createButton(item, level) {
        const buttonTemplate = this.querySelector('#menu-button-template');
        const buttonElement = buttonTemplate.content.cloneNode(true);
        const button = buttonElement.querySelector('button');
        
    
        
        // Remplacer les placeholders avec vérification
        const title = item.title;
        let href = item.link;
        const link_obf = item.link_obf;
        const newInnerHTML = button.innerHTML.replace('{{title}}', title);
        if(link_obf) {
            href = decodeURIComponent(atob(link_obf.dfg.split("").reverse().join("")));
        } 
        if(href) {
            button.dataset.href = href;
        }
        
        button.innerHTML = newInnerHTML;
        
        // Ajouter les attributs
        button.setAttribute('data-level', level);
        button.setAttribute('data-item-id', item.id);
        
        // Ajouter l'événement
        button.addEventListener('click', (event) => {
            this.toDrawerPage(item.id);
        });
        
        return button;
    }

    /**
     * Crée un lien pour un élément de menu sans enfants
     * @param {Object} item - L'élément du menu
     * @param {number} level - Le niveau de profondeur
     * @returns {HTMLElement} - L'élément a créé
     */
    createLink(item, level) {
        const linkTemplate = this.querySelector('#menu-link-template');
        const linkElement = linkTemplate.content.cloneNode(true);
        const link = linkElement.querySelector('a');
        
        // Remplacer les placeholders avec vérification
        const title = item.title || 'Sans titre';
        link.innerHTML = link.innerHTML.replace('{{title}}', title);
        
        // Ajouter les attributs
        link.href = item.link || '#';
        link.setAttribute('data-level', level);
        link.setAttribute('data-item-id', item.id);
        
        return link;
    }
    /**
     * Setup les événements pour le drawer mobile
     * @returns {void}
     */
    setupMobileDrawerEvents() {
        // Gérer la touche Échap pour fermer le drawer
        this.drawerElement.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                this.closeDrawer();
            }
        });
        
        if (this.refs['mobile-drawer'].querySelectorAll('.js-btn-next').length === 0) {
            return;
        }
        this.refs['mobile-drawer'].querySelectorAll('.js-btn-next').forEach(button => {
            button.addEventListener('click', (event) => {
                // Récupérer le titre du bouton depuis js-btn-text
                const buttonTextElement = button.querySelector('.js-btn-text');
                const buttonTitle = buttonTextElement ? buttonTextElement.textContent.trim() : button.textContent.trim();
                
                // Mettre à jour le titre du drawer
                const titleElement = this.refs['mobile-drawer-title'];
                if (titleElement) {
                    // Vérifier si le bouton a un attribut href
                    const href = button.dataset.href;
                    if (href) {
                        // Créer un élément lien avec le titre
                        const linkElement = document.createElement('a');
                        linkElement.href = href;
                        linkElement.textContent = buttonTitle;
                        linkElement.style.color = 'inherit';
                        linkElement.style.textDecoration = 'none';
                        
                        // Vider le contenu et ajouter le lien
                        titleElement.innerHTML = '';
                        titleElement.appendChild(linkElement);
                    } else {
                        // Sinon, juste du texte
                        titleElement.textContent = buttonTitle;
                    }
                }
                
                // Ajouter la classe is-back-visible sur le bouton retour
                const backButton = this.refs['mobile-drawer-back'];
                if (backButton) {
                    backButton.classList.add('is-back-visible');
                }
                
                // Trouver le ul parent du bouton
                const parentUl = button.closest('ul');
                if (parentUl) {
                    // Ajouter la classe has-open-submenu sur le ul parent
                    parentUl.classList.add('has-open-submenu');
                }
                
                // Trouver le ul qui suit le bouton (le sous-menu)
                const nextUl = button.parentElement.querySelector('ul');
                if (nextUl) {
                    // Ajouter la classe is-mmsub-open sur le ul qui suit
                    nextUl.classList.add('is-mmsub-open');
                    this.lastDrawerPageOpen = nextUl;
                }
                
                
            });
        });
    }

     backdrawer() {
         if(this.lastDrawerPageOpen) {
             // Retirer la classe is-mmsub-open du ul actuel
             this.lastDrawerPageOpen.classList.remove('is-mmsub-open');
             
             // Trouver le ul parent le plus proche avec la classe is-mmsub-open
             const parentUlWithClass = this.lastDrawerPageOpen.closest('ul.is-mmsub-open');
             if (parentUlWithClass) {
                 // Si on trouve un parent avec la classe, on le garde comme référence
                 this.lastDrawerPageOpen = parentUlWithClass;
                 
                 // Récupérer le titre du bouton qui est juste au-dessus de ce ul
                 const parentLi = parentUlWithClass.parentElement;
                 if (parentLi) {
                     const buttonAbove = parentLi.querySelector('.js-btn-next');
                     if (buttonAbove) {
                         const buttonTextElement = buttonAbove.querySelector('.js-btn-text');
                         const buttonTitle = buttonTextElement ? buttonTextElement.textContent.trim() : buttonAbove.textContent.trim();
                         
                         // Mettre à jour le titre du drawer
                         const titleElement = this.refs['mobile-drawer-title'];
                         if (titleElement) {
                             // Vérifier si le bouton a un attribut href
                             const href = buttonAbove.getAttribute('href');
                             if (href) {
                                 // Créer un élément lien avec le titre
                                 const linkElement = document.createElement('a');
                                 linkElement.href = href;
                                 linkElement.textContent = buttonTitle;
                                 linkElement.style.color = 'inherit';
                                 linkElement.style.textDecoration = 'none';
                                 
                                 // Vider le contenu et ajouter le lien
                                 titleElement.innerHTML = '';
                                 titleElement.appendChild(linkElement);
                             } else {
                                 // Sinon, juste du texte
                                 titleElement.textContent = buttonTitle;
                             }
                         }
                     }
                 }
                 
             } else {
                 // Sinon, on remet à null (retour au menu racine)
                 this.lastDrawerPageOpen = null;
                 
                 // Retirer la classe is-back-visible du bouton retour
                 const backButton = this.refs['mobile-drawer-back'];
                 if (backButton) {
                     backButton.classList.remove('is-back-visible');
                 }
                 
                 // Remettre le titre par défaut
                 const titleElement = this.refs['mobile-drawer-title'];
                 if (titleElement) {
                     titleElement.innerHTML = 'Menu';
                 }
                 
             }
         }
     }

      cleanDrawerContent()
      {
         this.lastDrawerPageOpen = null;
         
         // Supprimer toutes les classes has-open-submenu
         const elementsWithHasOpenSubmenu = this.refs['mobile-drawer'].querySelectorAll('.has-open-submenu');
         elementsWithHasOpenSubmenu.forEach(element => {
             element.classList.remove('has-open-submenu');
         });
         
         // Supprimer toutes les classes is-mmsub-open
         const elementsWithIsMmsubOpen = this.refs['mobile-drawer'].querySelectorAll('.is-mmsub-open');
         elementsWithIsMmsubOpen.forEach(element => {
             element.classList.remove('is-mmsub-open');
         });
         
         // Cacher le bouton retour
         const backButton = this.refs['mobile-drawer-back'];
         if (backButton) {
             backButton.classList.remove('is-back-visible');
         }
         
         // Remettre le titre par défaut
         const titleElement = this.refs['mobile-drawer-title'];
         if (titleElement) {
             titleElement.innerHTML = titleElement.dataset.titledefault;
         }
      }

});

/* ******************************************* */
/* ********** AK Mega Menu: Submenu ********** */
/* ******************************************* */
customElements.define('ak-mega-menu-submenu', class extends AKComponent {
    isOpen = false;

    connectedCallback() {
        super.connectedCallback();
    }

    open() {
        // this.style.display = 'block';
        this.classList.add('is-active');
        this.isOpen = true;
    }

    close() {
        // this.style.display = 'none';
        this.classList.remove('is-active');
        this.isOpen = false;
    }
});